import { useState } from "react";
import { Link } from "react-router-dom";
import { User, Bell, Shield, CalendarRange, ArrowRight } from "lucide-react";
import { AccountSettings } from "../../components/dashboard/settings/account-settings";
import { NotificationSettings } from "../../components/dashboard/settings/notification-settings";
import { SecuritySettings } from "../../components/dashboard/settings/security-settings";
import { cn } from "../../lib/util";

// Settings is now genuinely scoped to set-and-forget configuration. Availability
// management — an operational, near-daily task — has moved out to the top-level
// Schedule destination.
const TABS = [
  { id: "account", label: "Account", icon: User },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "security", label: "Security", icon: Shield },
] as const;

type TabId = (typeof TABS)[number]["id"];

const Settings = () => {
  const [activeTab, setActiveTab] = useState<TabId>("account");

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#B87775]">Configuration</p>
        <h1 className="font-serif text-3xl text-[#3a2e2c]">Settings</h1>
        <p className="mt-1 text-sm text-[#8a7a78]">
          Set-and-forget configuration for your account and the studio.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        {/* Side nav */}
        <nav aria-label="Settings sections" className="space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]",
                  active ? "bg-[#FBEFEF] font-medium text-[#B87775]" : "text-[#8a7a78] hover:bg-[#FBEFEF]/60"
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}

          {/* Signpost: where availability went */}
          <Link
            to="/control/schedule"
            className="mt-3 flex items-start gap-3 rounded-xl border border-dashed border-[#E3C9C8] p-3 text-sm text-[#8a7a78] transition hover:border-[#B87775]"
          >
            <CalendarRange className="mt-0.5 h-4 w-4 shrink-0 text-[#B87775]" />
            <span>
              Looking for availability &amp; hours? It now lives in{" "}
              <span className="font-medium text-[#B87775]">Schedule</span>.
              <ArrowRight className="ml-1 inline h-3.5 w-3.5 text-[#B87775]" />
            </span>
          </Link>
        </nav>

        {/* Panel */}
        <div className="rounded-2xl border border-[#EAD7D6] bg-white p-6">
          {activeTab === "account" && <AccountSettings />}
          {activeTab === "notifications" && <NotificationSettings />}
          {activeTab === "security" && <SecuritySettings />}
        </div>
      </div>
    </div>
  );
};

export default Settings;
